import { KeyMomentCategory, CardColor } from './types';

function sameMatchClock(a, b) {
  return a.half === b.half && a.minutes === b.minutes;
}

/** Same club for merge (API `teamUrl`); empty only merges with empty. */
function sameTeamUrl(a, b) {
  return (a.teamUrl ?? '') === (b.teamUrl ?? '');
}

function mergeCardSlice(slice) {
  const timestamps = slice.map((s) => s.timestamp).filter(Number.isFinite);
  const minTs = timestamps.length > 0 ? Math.min(...timestamps) : slice[0].timestamp;
  const mergedIncidenceIds = slice.map((s) => s.incidenceId);
  const hasRed = slice.some((s) => s.cardColor === CardColor.RED);
  const dominantColor = hasRed ? CardColor.RED : CardColor.YELLOW;
  const dominantMoment = slice.find((s) => s.cardColor === CardColor.RED)?.moment
    ?? slice[0].moment;

  const mergedSegments = slice.map((s) => ({
    incidenceId: s.incidenceId,
    moment: s.moment,
    cardColor: s.cardColor,
    playerName: s.playerName,
    iconUrl: s.iconUrl,
    teamUrl: s.teamUrl,
  }));

  const base = slice[0];
  return {
    ...base,
    timestamp: minTs,
    incidenceId: base.incidenceId,
    mergedIncidenceIds,
    mergedSegments,
    cardColor: dominantColor,
    moment: dominantMoment,
    teamUrl: slice.find((s) => s.teamUrl)?.teamUrl ?? base.teamUrl,
    iconUrl: slice.find((s) => s.iconUrl)?.iconUrl ?? base.iconUrl,
    playerName: slice.map((s) => s.playerName).filter(Boolean).join(' · '),
  };
}

function mergeSubstitutionSlice(slice) {
  const timestamps = slice.map((s) => s.timestamp).filter(Number.isFinite);
  const minTs = timestamps.length > 0 ? Math.min(...timestamps) : slice[0].timestamp;
  const mergedIncidenceIds = slice.map((s) => s.incidenceId);

  const mergedSegments = slice.map((s) => ({
    incidenceId: s.incidenceId,
    playerName: s.playerName,
    playerNameIn: s.playerNameIn,
    teamUrl: s.teamUrl,
    iconUrl: s.iconUrl,
  }));

  const base = slice[0];
  const firstWithTeam = slice.find((s) => s.teamUrl);
  const firstWithIcon = slice.find((s) => s.iconUrl);

  return {
    ...base,
    timestamp: minTs,
    incidenceId: base.incidenceId,
    mergedIncidenceIds,
    mergedSegments,
    teamUrl: firstWithTeam?.teamUrl ?? base.teamUrl,
    iconUrl: firstWithIcon?.iconUrl ?? base.iconUrl,
    playerName: slice.map((s) => s.playerName).filter(Boolean).join(' · '),
    playerNameIn: slice.map((s) => s.playerNameIn).filter(Boolean).join(' · '),
  };
}

/**
 * Collapses consecutive key moments that share the same match clock (period + minute)
 * for **cards** (same team + same card colour) and **substitutions** (same team) only.
 * Assumes `items` are sorted by `timestamp` ascending.
 */
export function mergeKeyMomentsSameMinute(items) {
  if (!Array.isArray(items) || items.length < 2) {
    return items;
  }

  const out = [];
  let i = 0;

  while (i < items.length) {
    const cur = items[i];
    const mergeable = cur.category === KeyMomentCategory.CARD
      || cur.category === KeyMomentCategory.SUBSTITUTION;

    if (mergeable) {
      let j = i + 1;
      while (j < items.length) {
        const next = items[j];
        if (next.category !== cur.category) break;
        if (!sameMatchClock(next, cur)) break;
        if (cur.category === KeyMomentCategory.CARD) {
          if (!sameTeamUrl(cur, next)) break;
          if (next.cardColor !== cur.cardColor) break;
        }
        if (cur.category === KeyMomentCategory.SUBSTITUTION && !sameTeamUrl(cur, next)) {
          break;
        }
        j += 1;
      }

      if (j === i + 1) {
        out.push(cur);
      } else {
        const slice = items.slice(i, j);
        out.push(
          cur.category === KeyMomentCategory.CARD
            ? mergeCardSlice(slice)
            : mergeSubstitutionSlice(slice),
        );
      }
      i = j;
    } else {
      out.push(cur);
      i += 1;
    }
  }

  return out;
}

/** True if any source incidence of this row is outside the DVR window. */
export function keyMomentRowTouchesUnavailable(item, unavailableIds) {
  if (!unavailableIds?.size || !item) return false;
  const ids = item.mergedIncidenceIds?.length > 0
    ? item.mergedIncidenceIds
    : [item.incidenceId];
  return ids.some((id) => unavailableIds.has(id));
}
